import {useLayoutEffect, useRef} from 'react';
import ProjectEvidence, {emphasizeEvidence, projectEvidence} from './ProjectEvidence';
import s from './index.module.less';

const SummaryDetailDialog = ({item, lang, onDismiss}) => {
    const dialogRef = useRef(null);
    const titleRef = useRef(null);
    const card = item.payload;
    const isProject = item.type === 'project';
    const emphasis = isProject ? projectEvidence[card.title]?.emphasis : undefined;
    const labels = lang === 'zh'
        ? {project: '项目详情', domain: '能力域', close: '关闭详情', overview: '项目概述', result: '验证与成果', details: '项目说明与技术细节'}
        : lang === 'ja'
            ? {project: 'プロジェクト詳細', domain: '専門領域', close: '詳細を閉じる', overview: '概要', result: '検証と成果', details: '詳細'}
            : {project: 'Project details', domain: 'Capability', close: 'Close details', overview: 'Overview', result: 'Evidence & outcomes', details: 'Project & technical details'};

    useLayoutEffect(() => {
        const dialog = dialogRef.current;
        const trigger = document.activeElement;
        const scrollRoot = document.getElementById('ushouldknow-scroll-root');
        const previousOverflow = scrollRoot?.style.overflow;
        if (scrollRoot) scrollRoot.style.overflow = 'hidden';
        dialog.showModal();
        titleRef.current?.focus({preventScroll: true});
        return () => {
            dialog.close();
            if (scrollRoot) scrollRoot.style.overflow = previousOverflow;
            if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus({preventScroll: true});
        };
    }, []);

    const dismissBackdrop = (event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onDismiss();
    };

    const keepFocusInDialog = (event) => {
        if (event.key !== 'Tab') return;
        const targets = Array.from(dialogRef.current.querySelectorAll('button, a[href], [tabindex="0"]'));
        const first = targets[0];
        const last = targets[targets.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !targets.includes(active))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    };

    return <dialog
        ref={dialogRef}
        className={s.summaryDialog}
        aria-labelledby='summary-dialog-title'
        onCancel={(event) => { event.preventDefault(); onDismiss(); }}
        onClick={dismissBackdrop}
        onKeyDown={keepFocusInDialog}
    >
        <header className={s.summaryDialogHeader}>
            <div>
                <p className={s.summaryDialogCategory}>{isProject ? labels.project : card.category || labels.domain}</p>
                <h2 id='summary-dialog-title' ref={titleRef} tabIndex={-1}>{card.title}</h2>
                {card.period ? <p className={s.summaryDialogPeriod}>{card.period}</p> : null}
            </div>
            <button type='button' className={s.summaryDialogClose} onClick={onDismiss} aria-label={labels.close}>×</button>
        </header>
        <div className={s.summaryDialogBody} tabIndex={0} role='region' aria-label={isProject ? labels.details : labels.domain}>
            {card.metric ? <ProjectEvidence card={card} heading={labels.result} /> : null}
            {card.keywords ? <p className={s.summaryDialogKeywords}>{card.keywords}</p> : null}
            {card.desc ? <section>
                <h3>{labels.overview}</h3>
                <p>{emphasizeEvidence(card.desc, emphasis)}</p>
            </section> : null}
            {card.points?.length ? <section>
                {isProject ? <h3>{labels.details}</h3> : null}
                <ul>{card.points.map((point) => <li key={point}>{emphasizeEvidence(point, emphasis)}</li>)}</ul>
            </section> : null}
            {card.labels?.length || card.github ? <footer className={s.summaryDialogFooter}>
                {card.labels?.length ? <div>{card.labels.map((label) => <span key={label}>{label}</span>)}</div> : null}
                {card.github ? <a href={card.github} target='_blank' rel='noreferrer'>GitHub ↗</a> : null}
            </footer> : null}
        </div>
    </dialog>;
};

export default SummaryDetailDialog;
